import React, { useState, useEffect } from 'react';
import generateInvoicePdf from '../utils/pdfGenerator'; // 1. Importer la fonction

// 2. Récupérer appConfig dans les props
const BillingView = ({ invoices = [], patients = [], appConfig, onEditInvoice }) => {
    const [selectedTemplate, setSelectedTemplate] = useState(appConfig?.defaultInvoiceTemplate || 'classic');

    useEffect(() => {
        const saved = localStorage.getItem('invoiceTemplatePreference');
        if (!appConfig?.defaultInvoiceTemplate && saved) setSelectedTemplate(saved);
    }, [appConfig?.defaultInvoiceTemplate]);

    useEffect(() => {
        localStorage.setItem('invoiceTemplatePreference', selectedTemplate);
    }, [selectedTemplate]);

    const availableTemplates = [
        { id: 'classic', name: 'Classique', description: 'Base neutre, claire, professionnelle', free: true },
        { id: 'modern', name: 'Moderne', description: 'Bande colorée et mise en page plus dynamique', free: true },
        { id: 'haiti', name: 'Haïti', description: 'Style local avec couleurs chaudes et en-tête identitaire', free: false }
    ];

    const plan = appConfig?.plan || 'simple';
    const canUseTemplate = (template) => template.free || plan !== 'simple';

    const styles = {
        container: { padding: '20px' },
        header: { marginBottom: '20px' },
        invoiceList: { listStyle: 'none', padding: '0' },
        invoiceItem: {
            backgroundColor: '#fff',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '10px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        },
        status: {
            padding: '5px 10px',
            borderRadius: '12px',
            color: '#fff',
            fontSize: '0.9em'
        },
        statusUnpaid: { backgroundColor: '#ff4d4f' },
        statusPaid: { backgroundColor: '#52c41a' },
        downloadButton: {
            marginLeft: '15px',
            padding: '5px 10px',
            cursor: 'pointer',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: '#f0f0f0'
        }
    };

    const getPatient = (patientId) => {
        return patients.find(p => p.id === patientId);
    };

    // 3. Fonction pour gérer le téléchargement
    const handleDownload = (invoice) => {
        const patient = getPatient(invoice.patientId);
        if (patient && appConfig) {
            generateInvoicePdf(invoice, patient, appConfig, selectedTemplate);
        } else {
            console.error("Impossible de générer le PDF : patient ou configuration du centre manquant.");
            alert("Erreur : Impossible de générer le PDF. Les informations du patient ou du centre sont manquantes.");
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={styles.header}>Module de Facturation</h2>
            <div style={{ marginBottom: 20, display: 'grid', gap: 10 }}>
                <h3>Choisir un modèle de facture</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                    {availableTemplates.map(template => {
                        const locked = !canUseTemplate(template);
                        return (
                            <button
                                key={template.id}
                                type="button"
                                onClick={() => {
                                    if (!locked) {
                                        setSelectedTemplate(template.id);
                                    } else {
                                        alert('Ce modèle est réservé à un plan supérieur.');
                                    }
                                }}
                                disabled={locked}
                                style={{
                                    padding: '16px',
                                    borderRadius: '12px',
                                    border: selectedTemplate === template.id ? '2px solid #0d3b66' : '1px solid #d1d5db',
                                    backgroundColor: locked ? '#f3f4f6' : '#ffffff',
                                    cursor: locked ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    minHeight: 120,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between'
                                }}
                            >
                                <div>
                                    <strong>{template.name}</strong>
                                    <p style={{ margin: '8px 0 0', color: '#475569' }}>{template.description}</p>
                                </div>
                                <div style={{ marginTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    {locked ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>Premium</span> : <span style={{ color: '#0f766e', fontWeight: 600 }}>Gratuit</span>}
                                    {selectedTemplate === template.id && !locked && <span style={{ color: '#0d3b66' }}>Sélectionné</span>}
                                </div>
                            </button>
                        );
                    })}
                </div>
                {plan === 'simple' && (
                    <p style={{ margin: 0, color: '#6b7280' }}><strong>Astuce :</strong> passez au plan Pro pour débloquer le modèle Haïti et d'autres styles premium.</p>
                )}
            </div>
            <ul style={styles.invoiceList}>
                {invoices.length > 0 ? (
                    invoices.map(invoice => (
                        <li key={invoice.id} style={styles.invoiceItem}>
                            <div>
                                <strong>Facture #{invoice.id.toString().slice(-6)}</strong> - {getPatient(invoice.patientId)?.name || 'Patient inconnu'}
                                <br />
                                <small>
                                    Date: {new Date(invoice.date).toLocaleDateString()} | Total: {Number(invoice.total || 0).toFixed(2)} {invoice.currency || 'USD'}
                                    {' | '}Paiement: {invoice.paymentMethod || 'Non renseigné'}
                                    {invoice.insurance ? ` | Assurance: ${invoice.insurance}` : ''}
                                    {invoice.paidAt ? ` | Réglée le: ${new Date(invoice.paidAt).toLocaleDateString()}` : ''}
                                </small>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <span style={{
                                    ...styles.status,
                                    ...(invoice.status === 'paid' ? styles.statusPaid : styles.statusUnpaid)
                                }}>
                                    {invoice.status === 'paid' ? 'Payée' : 'Non Payée'}
                                </span>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                    <button
                                        onClick={() => handleDownload(invoice)}
                                        style={styles.downloadButton}
                                        title="Télécharger en PDF"
                                    >
                                        PDF
                                    </button>
                                    <button
                                        onClick={() => onEditInvoice(invoice)}
                                        style={{ ...styles.downloadButton, backgroundColor: '#ececec' }}
                                        title="Modifier la facture"
                                    >
                                        Modifier
                                    </button>
                                </div>
                            </div>
                        </li>
                    ))
                ) : (
                    <p>Aucune facture pour le moment.</p>
                )}
            </ul>
        </div>
    );
};

export default BillingView;
